import { FC, JSX } from 'react';
import { Button, Stack } from '@mui/joy';
import { Loader } from '@consolidocs-ui/components';
import { CompanyFormApi } from './useCompanyForm';

interface WizardActionsProps {
  form: CompanyFormApi;
  onCancel: () => void;
}

const CancelButton: FC<{ onClick: () => void; disabled: boolean }> = ({
  onClick,
  disabled,
}) => (
  <Button
    type="button"
    variant="plain"
    color="neutral"
    onClick={onClick}
    disabled={disabled}
  >
    Cancel
  </Button>
);

const SubmitButton: FC<{
  label: string;
  onClick: () => void;
  loading: boolean;
}> = ({ label, onClick, loading }) => (
  <Button
    type="button"
    variant="solid"
    color="primary"
    onClick={onClick}
    loading={loading}
    loadingIndicator={<Loader size="sm" />}
  >
    {label}
  </Button>
);

/** Footer buttons for the wizard — the set shown depends on the flow phase. */
export const WizardActions: FC<WizardActionsProps> = ({
  form,
  onCancel,
}): JSX.Element => {
  const {
    createdOrgId,
    isGeneralEdit,
    isEdit,
    activeStep,
    isSubmitting,
    isProvisioning,
    goToNextStep,
    goToPreviousStep,
    handleSubmit,
    onDone,
  } = form;

  const buttons = () => {
    // The organization already exists — the only thing left is to wait for its
    // infrastructure, which the user is free to do elsewhere.
    if (createdOrgId) {
      return (
        <Button type="button" variant="solid" color="primary" onClick={onDone}>
          {isProvisioning ? 'Continue in background' : 'Done'}
        </Button>
      );
    }

    // General edit is a single step: save straight from the details form.
    if (isGeneralEdit) {
      return (
        <>
          <CancelButton onClick={onCancel} disabled={isSubmitting} />
          <SubmitButton
            label="Save Changes"
            onClick={handleSubmit}
            loading={isSubmitting}
          />
        </>
      );
    }

    // Create, or enterprise edit: stepped flow.
    return (
      <>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        {activeStep === 1 && (
          <Button
            type="button"
            variant="outlined"
            color="neutral"
            onClick={goToPreviousStep}
            disabled={isSubmitting}
          >
            Back
          </Button>
        )}
        {activeStep === 0 ? (
          <Button
            type="button"
            variant="solid"
            color="primary"
            onClick={goToNextStep}
          >
            Next
          </Button>
        ) : (
          <SubmitButton
            label={isEdit ? 'Save Changes' : 'Create Organization'}
            onClick={handleSubmit}
            loading={isSubmitting}
          />
        )}
      </>
    );
  };

  return (
    <Stack
      direction={{ xs: 'column-reverse', sm: 'row' }}
      spacing={1}
      justifyContent="flex-end"
      sx={{ mt: 3 }}
      className="wizard-actions"
    >
      {buttons()}
    </Stack>
  );
};

export default WizardActions;
